/**
 * Recharts colour tokens for the trend chart. Recharts strokes/fills need concrete
 * hex/rgba, not Tailwind classes, so these mirror the kit's RAG + trust vocabulary
 * (cellFormat.RAG_CLASSES, MetricCell's lock/divergent icons) as literal values.
 * Neutral shading/grid use alpha so one value reads on both light and dark.
 */

import type { Cell, Rag } from '../../types/metrics'

// RAG dot fills — emerald-600 / red-600 / slate-400 (match RAG_CLASSES).
export const RAG_HEX: Record<Rag, string> = {
  green: '#059669',
  red: '#dc2626',
  none: '#94a3b8',
}

// Series lines + envelope adornments. Chrome (grid, axis, surface, the neutral line)
// rides the theme tokens — Recharts writes these straight onto SVG attributes, where
// `var(--x)` resolves, so one value is correct in both themes. The RAG dots and the
// lock rings stay concrete: they are server truth, not chrome, and must not drift
// with a palette change.
export const TREND_HEX = {
  actual: 'var(--blue)', // the neutral Actual line (RAG rides the per-point dots)
  target: 'var(--mut2)', // dashed: the Target threshold line
  surface: 'var(--panel)', // the 2px ring that keeps a dot legible on the line
  divergent: '#f59e0b', // amber-500 ring: locked value restated since it froze
  lockedRing: '#64748b', // slate-500 ring: a locked month (per-dot cue, survives a lone lock)
  lockedShade: 'rgba(100,116,139,0.10)', // slate-500 @ 10% — locked-period band
  grid: 'var(--line2)', // hairline, SOLID — dashed here would compete with the target rule
} as const

/** A served Actual point's dot colour: grey for NULL, else its server-computed RAG. */
export function ragHex(cell: Cell): string {
  return cell.value === null ? RAG_HEX.none : RAG_HEX[cell.rag]
}
